'use client';

import type { ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { ScreenLayoutProvider } from '@/screen';
import { ScrollManagerProvider } from '@/contexts/ScrollManagerContext';
import { TabBarProvider } from '@/contexts/TabBarContext';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { SupportWidget } from '@/components/support/SupportWidget';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { DesktopContentWidthProvider } from '@/contexts/DesktopContentWidthContext';

/**
 * Mounts the provider chain + layout shell that every legacy
 * stack-nav screen expected:
 *  - ScrollManagerProvider — per-tab scroll memory.
 *  - TabBarProvider — state for the bottom-tab UI.
 *  - ScreenLayoutProvider — fixed-top/bottom registration +
 *    conditional TabBar render (mobile only).
 *  - DesktopLayout (>= 1024px) — desktop chrome: drops feed, top
 *    header, side nav, max-width content column. Mirrors what
 *    legacy AppContent.tsx did via `useIsDesktop()` branching.
 *
 * `minimal` is set when the route is part of the onboarding flow,
 * matching legacy behaviour (`navigation.state.activeTab ===
 * 'onboarding'` → `<DesktopLayout minimal>`).
 *
 * Use in any (auth) or (public) page.tsx that renders a full-screen
 * TMA-style screen. Marketing-style routes with their own layout
 * (public `/`, login pages) should NOT wrap here — ScreenLayoutProvider
 * absolute-positions and would collide with normal document scroll.
 */
function isOnboardingPath(pathname: string): boolean {
  return pathname.startsWith('/onboarding');
}

export function ScreenPage({ children }: { children: ReactNode }) {
  const isDesktop = useIsDesktop();
  const pathname = usePathname() ?? '/';
  const minimal = isOnboardingPath(pathname);

  const tree = (
    <ScrollManagerProvider>
      <TabBarProvider>
        <ScreenLayoutProvider>
          {children}
          <SupportWidget />
        </ScreenLayoutProvider>
      </TabBarProvider>
    </ScrollManagerProvider>
  );

  return (
    <DesktopContentWidthProvider>
      {isDesktop ? <DesktopLayout minimal={minimal}>{tree}</DesktopLayout> : tree}
    </DesktopContentWidthProvider>
  );
}
